/**
 * @file utils/cookies/library.js
 * @copyright 2017 CBC/Radio-Canada. All rights reserved.
 * This file contains methods that deal with browser cookies
 */
import {
    GUEST_COOKIE,
    AUTHED_COOKIE,
    LOCAL_COOKIE,
    RADIO_REGEX,
    COOKIE_DOMAIN,
} from "./constants";
import { isServer } from "../environmentDetection/library";

/**
 * This function creates a cookie
 * @param {string} name The name of the cookie
 * @param {string} value The value of the cookie
 * @param {int} days Amount of days before cookie expiration, by default
 * @returns {void}
 */
export function createFeedCookie(name, value, days) {
    if (isServer()) {
        return;
    }

    // Set the expiration date to 1000 days, unless specified otherwise
    const dayInSeconds = 86400000;
    const defaultExpiryDays = 1000;
    const expiration = (days ? days : defaultExpiryDays) * dayInSeconds;
    let date = new Date();
    date.setTime(date.getTime() + expiration);
    const expires = `; expires=${date.toUTCString()}`;

    // Set the cookie
    window.document.cookie = `${name}=${value}${expires}; path=/; domain=${COOKIE_DOMAIN}`;
}

/**
 * This function retrieves cookie information, if it's found
 * @param {string} name The name of the cookie
 * @returns {object} An object containing cookie information {name:"", value:""}
 */
export function getFeedCookie(name) {
    if (isServer()) {
        return;
    }

    const cookies = window.document.cookie.split(";");
    let requestedCookie = null;
    cookies.forEach((cookie) => {
        const cookieValues = cookie.split("=");
        if (cookieValues[0] && cookieValues[0].trim() === name) {
            requestedCookie = {
                name: cookieValues[0],
                value: cookieValues[1],
            };
        }
    });
    return requestedCookie;
}

/**
 * This function retrieves cookie information for all cookies of the same name, if found
 * @param {string} name The name of the cookies
 * @returns {object} An array containing cookie information objects { name, value }
 */
export function getAllFeedCookiesWithName(name) {
    if (isServer()) {
        return;
    }

    const cookies = window.document.cookie.split(";");
    let requestedCookies = [];
    cookies.forEach((cookie) => {
        const cookieValues = cookie.split("=");
        if (cookieValues[0] && cookieValues[0].trim() === name) {
            return requestedCookies.push({
                name: cookieValues[0],
                value: cookieValues[1],
            });
        }
    });
    return requestedCookies;
}

/**
 * This function can either update an existing cookie, or create it if it doesn't exist
 * @param {string} name The name of the cookie to be updated
 * @param {string} value The value of the cookie to be updated
 * @returns {void}
 */
export function setFeedCookie(name, value, days) {
    if (isServer()) {
        return;
    }
    const cookieExists = getFeedCookie(name);
    if (cookieExists) {
        //Delete the cookie if it exists, by setting the expiration date in the past
        document.cookie = `${cookieExists.name} +'=; Path=/; Expires=Thu, 01 Jan 1970 00:00:01 GMT;`;
    }

    //Create the cookie with a new value
    createFeedCookie(name, value, days);
}

/**
 * This function searches for a specific cookie embedded in the cookie
 * @param {string} cookieName The name of the cookie that needs to be stored in the cookie, ex: cbclocal
 * @param {string} cookieRegex The regex used to match a specific cookie
 * @returns {string} The value of the cookie that was stored in the cookie
 */
function getCookieFromStorage(cookieName, cookieRegex) {
    // The value of the cookie is of the following format:
    // Example cookie: cbclocal = {"value": "weather%3Ds0000458%26radio%3Dtoronto%26news%3Dtoronto%26tv%3Dtoronto%26region%3Dtoronto"}
    const cookie = getFeedCookie(cookieName);
    const regex = new RegExp(`${cookieRegex}`);
    const regexMatch = cookie ? cookie.value.match(regex) : null;

    return regexMatch && regexMatch.length ? regexMatch[1] : null;
}

/**
 * This function specifically searches for the radio information in the cbclocal cookie
 * @returns {string} The name of the selected radio that was stored in the cookie
 */
export function getRadioFromStorage() {
    return getCookieFromStorage(LOCAL_COOKIE, RADIO_REGEX);
}

/**

 * This function stores a cookie to indicate if CBC News App was downloaded
 * @returns {void}
 */
export function storeAppDownloadedCookie() {
    setFeedCookie("appDownloadedCookie", null);
}

/**
 * This function checks whether to show the mobile app call to action (MobileAppCta component)
 * @returns {boolean} The indication whether to show mobile CTA
 */
export function appDownloadedCookie() {
    const cookie = getFeedCookie("appDownloadedCookie");

    if (cookie) {
        return true;
    }

    return false;
}

/**
 * This function stores a cookie to say that the CTA was shown
 * @returns {void}
 */
export function storeCtaCloseCookie() {
    const expiryTwoWeeks = 14;
    setFeedCookie("ctaCloseCookie", null, expiryTwoWeeks);
}

/**
 * This function checks whether to show the mobile app call to action (MobileAppCta component)
 * @returns {boolean} The indication whether to show mobile CTA
 */
export function showMobileAppCta() {
    const cookie = getFeedCookie("ctaCloseCookie");

    if (!cookie) {
        return true;
    }

    return false;
}
/**
 * This function will get a user object representing the user type and id from the cookie
 *  @param {void}
 *  @returns {object || null}
 */
export function getUserFromCookie() {
    //prioritize plus over visitor
    let plusCookie = getFeedCookie(AUTHED_COOKIE);
    if (plusCookie) {
        return {
            type: plusCookie.name.trim(),
            id: plusCookie.value.trim(),
        };
    }

    let visitorCookie = getFeedCookie(GUEST_COOKIE);
    if (visitorCookie) {
        return {
            type: visitorCookie.name.trim(),
            id: visitorCookie.value.trim(),
        };
    }

    return null;
}
